import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  FileText,
  Loader2,
  Plus,
  ScanSearch,
} from "lucide-react";

import { useQueryClient } from "@tanstack/react-query";

import { useAdminOrganizations } from "@/hooks/queries/useAdminOrganizations";
import {
  useWorkAdvisoryReviewList,
  useWorkAdvisoryReviewReport,
} from "@/hooks/queries/useWorkAdvisoryReview";
import {
  useGenerateWorkAdvisoryDraft,
  usePublishWorkAdvisoryReport,
  useSaveWorkAdvisoryDraft,
} from "@/hooks/mutations/useWorkAdvisoryReviewMutations";
import {
  usePrepareReport,
  useReportPreparation,
} from "@/hooks/queries/useWorkAdvisoryBases";
import { workAdvisoryKeys } from "@/lib/query-keys";
import { formatPeriod } from "@/lib/workadvisory-period";
import {
  byScoreDesc,
  HIDDEN_REASON_LABELS,
  isPublishable,
} from "@/lib/workadvisory-score";
import { formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { WorkAdvisoryWorkEditor } from "@/components/admin/WorkAdvisoryWorkEditor";
import type {
  ReportedWork,
  WorkAdvisoryReportData,
} from "@/types/workadvisory";

/** Genera el borrador de una semana. Sin fecha toma la última semana cerrada. */
function DraftGenerator() {
  const { data: organizations } = useAdminOrganizations();
  const [organizationId, setOrganizationId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const generate = useGenerateWorkAdvisoryDraft();

  const run = () => {
    if (!organizationId) return toast.error("Elige una empresa");
    generate.mutate(
      { organizationId, periodStart: periodStart || undefined },
      {
        onSuccess: () => toast.success("Borrador generado"),
        onError: () => toast.error("No se pudo generar el borrador"),
      },
    );
  };

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-card p-4">
      <label className="flex flex-col gap-1 text-xs font-semibold">
        Empresa
        <select
          value={organizationId}
          onChange={(event) => setOrganizationId(event.target.value)}
          className="min-w-56 rounded-md border bg-background px-2.5 py-1.5 text-sm font-normal"
        >
          <option value="">Elegir…</option>
          {organizations?.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-semibold">
        Semana (cualquier día; se normaliza al lunes)
        <input
          type="date"
          value={periodStart}
          onChange={(event) => setPeriodStart(event.target.value)}
          className="rounded-md border bg-background px-2.5 py-1.5 text-sm font-normal"
        />
      </label>
      <Button onClick={run} disabled={generate.isPending} className="gap-2">
        {generate.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Plus className="size-4" />
        )}
        Generar borrador
      </Button>
      <p className="text-xs text-muted-foreground">
        Sin fecha toma la última semana cerrada. Regenerar un borrador lo rehace;
        un informe ya publicado no se toca.
      </p>
    </div>
  );
}

export default function AdminWorkAdvisory() {
  const { data: reports, isLoading } = useWorkAdvisoryReviewList();
  const [selectedId, setSelectedId] = useState<number>();
  const { data: report } = useWorkAdvisoryReviewReport(selectedId);
  const save = useSaveWorkAdvisoryDraft();
  const publish = usePublishWorkAdvisoryReport();
  const prepare = usePrepareReport();
  const { data: preparation } = useReportPreparation(selectedId);
  const queryClient = useQueryClient();

  // El borrador se edita en memoria y se guarda entero: lo toca una persona a la
  // vez, así que partirlo en updates por obra sería maquinaria de más.
  const [draft, setDraft] = useState<WorkAdvisoryReportData>();
  // Los cambios viven en memoria hasta Guardar. Sin marcarlo, mover una obra y
  // recargar la perdía sin que nada lo avisara.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(report?.workadvisoryreport_data);
    setDirty(false);
  }, [report]);

  // Al terminar la preparación el informe se regeneró en el servidor con los
  // cruces congelados dentro, así que hay que releerlo: si no, la pantalla sigue
  // mostrando el borrador de antes y publicaría sin los análisis.
  const wasPreparing = useRef(false);
  useEffect(() => {
    if (preparation?.running) {
      wasPreparing.current = true;
      return;
    }
    if (!wasPreparing.current) return;
    wasPreparing.current = false;
    queryClient.invalidateQueries({ queryKey: workAdvisoryKeys.all });
    toast.success("Informe preparado");
  }, [preparation?.running, queryClient]);

  // El navegador avisa antes de irse con cambios sin guardar.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const editDraft = (
    update: (current: WorkAdvisoryReportData) => WorkAdvisoryReportData,
  ) => {
    setDirty(true);
    setDraft((current) => (current ? update(current) : current));
  };

  const isPublished = Boolean(report?.workadvisoryreport_publishedat);
  /**
   * Las obras del borrador sin cruce: es exactamente lo que hará el lote. Incluye
   * las ocultas por las bases, que también viven en `works` — por eso el número
   * puede no cuadrar con lo que se ve en la pestaña abierta.
   */
  const pendingWorks = draft?.works.filter((work) => !work.analysis) ?? [];
  const pending = useMemo(
    () => reports?.filter((r) => !r.workadvisoryreport_publishedat).length ?? 0,
    [reports],
  );

  // Las obras del informe se parten en dos vistas: lo que el cliente verá, y lo
  // que las bases descartaron. Siguen en la misma lista `works` — ocultarlas es
  // una decisión derivada, no un traslado.
  const visibleWorks = draft?.works.filter(isPublishable) ?? [];
  const hiddenWorks = draft?.works.filter((work) => !isPublishable(work)) ?? [];

  const patchWork = (
    list: "works" | "excludedWorks",
    contractingId: number,
    patch: Partial<ReportedWork>,
  ) =>
    editDraft((current) => ({
      ...current,
      [list]: current[list].map((work) =>
        work.contractingId === contractingId ? { ...work, ...patch } : work,
      ),
    }));

  const moveWork = (from: "works" | "excludedWorks", contractingId: number) =>
    editDraft((current) => {
      const to = from === "works" ? "excludedWorks" : "works";
      const work = current[from].find((w) => w.contractingId === contractingId);
      if (!work) return current;
      return {
        ...current,
        [from]: current[from].filter((w) => w.contractingId !== contractingId),
        // Se reordena la lista destino: una obra incluida tiene que caer donde
        // le toca por score, no al final.
        [to]: [...current[to], work].sort(byScoreDesc),
      };
    });

  const removeWork = (list: "works" | "excludedWorks", contractingId: number) =>
    editDraft((current) => ({
      ...current,
      [list]: current[list].filter((w) => w.contractingId !== contractingId),
    }));

  const onSave = () => {
    if (!selectedId || !draft) return;
    save.mutate(
      { reportId: selectedId, data: draft },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success("Informe guardado");
        },
        onError: () => toast.error("No se pudo guardar"),
      },
    );
  };

  const onPublish = () => {
    if (!selectedId || !draft) return;
    // Se guarda antes de publicar: publicar con cambios sin guardar mostraría al
    // cliente una versión distinta de la que se está viendo en pantalla.
    save.mutate(
      { reportId: selectedId, data: draft },
      {
        onSuccess: () => {
          setDirty(false);
          publish.mutate(selectedId, {
            onSuccess: () => toast.success("Informe publicado"),
            onError: () => toast.error("No se pudo publicar"),
          });
        },
        onError: () => toast.error("No se pudo guardar antes de publicar"),
      },
    );
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 md:px-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">
          Informe semanal de obras
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          El cliente solo ve lo publicado. {pending} borrador(es) pendiente(s).
        </p>
      </div>

      <DraftGenerator />

      {isLoading ? (
        <Skeleton className="h-24 rounded-xl" />
      ) : (
        <div className="flex flex-wrap gap-2">
          {reports?.map((row) => (
            <button
              key={row.workadvisoryreport_id}
              onClick={() => setSelectedId(row.workadvisoryreport_id)}
              className={cn(
                "rounded-xl border px-3 py-2 text-left text-xs transition-colors",
                selectedId === row.workadvisoryreport_id
                  ? "border-primary bg-primary/5"
                  : "hover:bg-accent",
              )}
            >
              <div className="font-bold">{row.organization.name}</div>
              <div className="text-muted-foreground">
                {formatPeriod(
                  row.workadvisoryreport_periodstart,
                  row.workadvisoryreport_periodend,
                )}
              </div>
              <div
                className={cn(
                  "mt-1 flex items-center gap-1 font-semibold",
                  row.workadvisoryreport_publishedat
                    ? "text-emerald-600"
                    : "text-amber-600",
                )}
              >
                {row.workadvisoryreport_publishedat ? (
                  <>
                    <CheckCircle2 className="size-3" /> Publicado
                  </>
                ) : (
                  <>
                    <FileText className="size-3" /> Borrador
                  </>
                )}
              </div>
            </button>
          ))}
          {reports?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Todavía no hay informes. Genera el primero arriba.
            </p>
          )}
        </div>
      )}

      {report && draft && (
        <div className="space-y-4">
          <div className="sticky top-16 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card/95 p-4 backdrop-blur-md">
            <div>
              <p className="text-sm font-bold">
                {report.organization.name} ·{" "}
                {formatPeriod(
                  report.workadvisoryreport_periodstart,
                  report.workadvisoryreport_periodend,
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {draft.works.length} obra(s) en el informe ·{" "}
                {draft.excludedWorks.length} fuera del informe
                {isPublished && " · ya publicado: los cambios se ven al guardar"}
              </p>
              {dirty && (
                <p className="mt-0.5 text-xs font-bold text-amber-600">
                  Cambios sin guardar
                </p>
              )}
              {preparation?.running && (
                <p className="mt-0.5 flex items-center gap-1.5 text-xs font-bold text-amber-600">
                  <Loader2 className="size-3 animate-spin" />
                  {preparation.done} de {preparation.total} listas ·{" "}
                  {preparation.current.length} en curso
                </p>
              )}
              {!preparation?.running && preparation?.failures.length ? (
                <p className="mt-0.5 text-xs font-bold text-red-600">
                  {preparation.failures.length} obra(s) sin preparar:{" "}
                  {preparation.failures[0]!.reason}
                </p>
              ) : null}
            </div>
            <div className="flex gap-2">
              {/* Solo las que faltan: el lote salta las que ya tienen cruce, así
                  que anunciar el total prometía trabajo que no iba a hacer. Con
                  cero pendientes desaparece solo. */}
              {!isPublished && pendingWorks.length > 0 && (
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={preparation?.running || prepare.isPending || dirty}
                  title={
                    dirty
                      ? "Guarda los cambios antes de preparar"
                      : `Sin analizar: ${pendingWorks.map((work) => work.nomenclature).join(", ")}`
                  }
                  onClick={() =>
                    prepare.mutate(
                      { reportId: selectedId! },
                      {
                        onSuccess: () =>
                          toast.info(
                            "Preparando las obras una por una. Son varios minutos cada una.",
                          ),
                        onError: (err: any) =>
                          toast.error(err?.error ?? "No se pudo preparar"),
                      },
                    )
                  }
                >
                  {preparation?.running || prepare.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ScanSearch className="size-4" />
                  )}
                  Preparar {pendingWorks.length}{" "}
                  {pendingWorks.length === 1 ? "obra" : "obras"}
                </Button>
              )}
              {/* Solo para publicados: relee TODAS las bases y rehace TODOS los
                  cruces. Es la única forma de que una mejora en la extracción
                  llegue a un informe que ya no se puede regenerar. Pide
                  confirmación porque el cliente ya lo leyó. */}
              {isPublished && draft.works.length > 0 && (
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={preparation?.running || prepare.isPending || dirty}
                  title={
                    dirty ? "Guarda los cambios antes de reanalizar" : undefined
                  }
                  onClick={() => {
                    if (
                      !confirm(
                        `Se van a releer las bases y rehacer el cruce de las ${draft.works.length} obras. El informe ya está publicado: el cliente verá los puntajes nuevos. Tus veredictos, scores y notas se conservan.`,
                      )
                    )
                      return;
                    prepare.mutate(
                      { reportId: selectedId!, force: true },
                      {
                        onSuccess: () =>
                          toast.info(
                            "Reanalizando todo el informe. Son varios minutos por obra.",
                          ),
                        onError: (err: any) =>
                          toast.error(err?.error ?? "No se pudo reanalizar"),
                      },
                    );
                  }}
                >
                  {preparation?.running || prepare.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ScanSearch className="size-4" />
                  )}
                  Reanalizar las {draft.works.length} obras
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onSave}
                disabled={save.isPending || !dirty}
              >
                {save.isPending && (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                )}
                Guardar
              </Button>
              {!isPublished && (
                <Button
                  onClick={onPublish}
                  disabled={save.isPending || publish.isPending}
                >
                  {publish.isPending && (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  )}
                  Publicar
                </Button>
              )}
            </div>
          </div>

          {/* En pestañas y no una debajo de otra: son dos listas largas y
              revisar una no requiere ver la otra. */}
          <Tabs defaultValue="works">
            <TabsList>
              <TabsTrigger value="works">
                Obras del informe ({visibleWorks.length})
              </TabsTrigger>
              <TabsTrigger value="hidden">
                Ocultas por las bases ({hiddenWorks.length})
              </TabsTrigger>
              <TabsTrigger value="excluded">
                Fuera del informe ({draft.excludedWorks.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="works" className="space-y-3">
              {visibleWorks.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground">
                  Ninguna obra pasó los filtros esta semana.
                </p>
              ) : (
                visibleWorks.map((work) => (
                  <WorkAdvisoryWorkEditor
                    key={work.contractingId}
                    work={work}
                    organizationId={report.organizationId}
                    preparing={preparation?.running}
                    moveLabel="Sacar del informe"
                    moveIcon="down"
                    onChange={(patch) =>
                      patchWork("works", work.contractingId, patch)
                    }
                    onMove={() => moveWork("works", work.contractingId)}
                    onRemove={() => removeWork("works", work.contractingId)}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="hidden" className="space-y-3">
              <p className="text-xs text-muted-foreground">
                No se le muestran al cliente al publicar, pero no se borran: la
                razón sale de las bases y puede cambiar cuando se vuelvan a leer.
                Si sabes que una sí le sirve, márcala para publicar igual.
              </p>
              {hiddenWorks.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground">
                  Ninguna. Todas las obras del informe se publican.
                </p>
              ) : (
                hiddenWorks.map((work) => (
                  <div key={work.contractingId} className="space-y-2">
                    <div className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
                      <span className="text-xs font-bold text-amber-800 dark:text-amber-300">
                        {HIDDEN_REASON_LABELS[work.hiddenReason!] ??
                          work.hiddenReason}
                        {work.declaredAmount !== null &&
                          ` · las bases dicen ${formatMoney(work.declaredAmount, "S/ ")}`}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          patchWork("works", work.contractingId, {
                            forcePublish: true,
                          })
                        }
                      >
                        Publicar igual
                      </Button>
                    </div>
                    <WorkAdvisoryWorkEditor
                      work={work}
                      organizationId={report.organizationId}
                      preparing={preparation?.running}
                      moveLabel="Sacar del informe"
                      moveIcon="down"
                      onChange={(patch) =>
                        patchWork("works", work.contractingId, patch)
                      }
                      onMove={() => moveWork("works", work.contractingId)}
                      onRemove={() => removeWork("works", work.contractingId)}
                    />
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="excluded" className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Las que SEACE publicó sin valor referencial, más las que bajaste
                a mano. Están clasificadas por la IA igual que las del informe,
                así que su encaje sirve para decidir si alguna merece entrar.
              </p>
              {draft.excludedWorks.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground">
                  Ninguna esta semana.
                </p>
              ) : (
                draft.excludedWorks.map((work) => (
                  <WorkAdvisoryWorkEditor
                    key={work.contractingId}
                    work={work}
                    organizationId={report.organizationId}
                    moveLabel="Incluir en el informe"
                    moveIcon="up"
                    onChange={(patch) =>
                      patchWork("excludedWorks", work.contractingId, patch)
                    }
                    onMove={() => moveWork("excludedWorks", work.contractingId)}
                    onRemove={() =>
                      removeWork("excludedWorks", work.contractingId)
                    }
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
